import { useUploadStore } from "@components/FileUpload/uploadStore";
import { useNotification } from "@context/NotificationContext";
import { useAuthStore } from "@persisted-state/auth/auth.store";
import type { NormalGatepassRequestPayload } from "@services/gatepass/types";
import { submitGatepassRequest } from "@services/gatepassApi";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";
import type { AxiosError } from "axios";
import { t } from "i18next";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { mapGatepassFormToPayload } from "../components/utils";
import type { getNormalGatepassFormSchema } from "../schema/normalGatepassFormSchema";
import type { getOffshoreGatepassFormSchema } from "../schema/offshoreGatepassFormSchema";
import type { getTruckGatepassFormSchema } from "../schema/truckGatepassFormSchema";

export type GatepassFormValues =
  | z.infer<ReturnType<typeof getNormalGatepassFormSchema>>
  | z.infer<ReturnType<typeof getOffshoreGatepassFormSchema>>
  | z.infer<ReturnType<typeof getTruckGatepassFormSchema>>;

interface GatepassRequestProps {
  // Latest values from form + store (getValues and zustand)
  getFormValues: () => GatepassFormValues;
  resetForm: () => void;
  securityClearanceId: string;
  onSubmitError?: (error: unknown) => void;
}

export interface NormalGatepassResult {
  onSubmit: () => Promise<void>;
}

type ErrorResponse = {
  detail?: string;
  message?: string;
  errors?: {
    errorMessage?: string;
  };
};

const TRUCK_GATEPASS_CATEGORY = "TRUCK";
const DELIVERY_NOTES_FIELD_KEY = "deliveryNotesDocuments";

const getUploadUrl = (entry: unknown): string => {
  if (!entry || typeof entry !== "object") return "";
  const normalized = entry as Record<string, unknown>;
  return String(normalized.url ?? normalized.documentUrl ?? normalized.blobUrl ?? "").trim();
};

const hasMissingDeliveryNoteUrls = (formValues: GatepassFormValues): boolean => {
  if (String(formValues.gatepassCategory ?? "").toUpperCase() !== TRUCK_GATEPASS_CATEGORY) {
    return false;
  }

  const deliveryNotes = (formValues as { deliveryNotesDocuments?: unknown[] })
    .deliveryNotesDocuments;
  if (!Array.isArray(deliveryNotes) || deliveryNotes.length === 0) return false;
  return deliveryNotes.some((entry) => !getUploadUrl(entry));
};

const shouldResetUploadedDocuments = (error: unknown): boolean => {
  const axiosError = error as AxiosError<ErrorResponse>;
  const status = axiosError?.response?.status;
  const detail = String(
    axiosError?.response?.data?.errors?.errorMessage ??
      axiosError?.response?.data?.detail ??
      axiosError?.message ??
      "",
  ).toLowerCase();

  if (typeof status === "number" && status >= 500) return true;
  return status === 404 && detail.includes("document") && detail.includes("not found");
};

export const getSubmitErrorMessage = (error: unknown): string | undefined => {
  const axiosError = error as AxiosError<ErrorResponse>;
  return (
    axiosError?.response?.data?.errors?.errorMessage ||
    axiosError?.response?.data?.detail ||
    undefined
  );
};

const GATEPASS_PAGE_KEY = "gatepass-page";

export const useSubmitGatepassRequest = (props: GatepassRequestProps): NormalGatepassResult => {
  const { resetForm, getFormValues, securityClearanceId, onSubmitError } = props;
  const userRoles = useAuthStore((state) => state.user?.roles ?? []);
  const isIndividualUser =
    userRoles.includes("security-applicant") || userRoles.includes("uae-pass-user");

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { notify } = useNotification();
  // add all needed types
  const gatepassRequest = useMutation<unknown, unknown, NormalGatepassRequestPayload>({
    mutationFn: (payload) => {
      return runTransaction(
        {
          module: "gatepass",
          screen: "GatepassRequest",
          action: "submit_gatepass_request",
          entityType: "gatepass",
        },
        (transactionId) =>
          submitGatepassRequest({
            service: "bff-gatepass",
            payload,
            ...(transactionId ? { transactionId } : {}),
          }),
      );
    },
    onSuccess: async (_data, variables) => {
      resetForm();
      // New requests will show on the first page in the grid.
      localStorage.setItem(GATEPASS_PAGE_KEY, "1");
      await queryClient.invalidateQueries({ queryKey: ["gatepasses"], exact: false });
      navigate("/gatepass");
      const payload = variables as NormalGatepassRequestPayload;
      const isOffshore = String(payload.gatepassCategory ?? "").toUpperCase() === "OFFSHORE";
      const successDescriptionKey =
        !isOffshore && payload.fottEntry === true
          ? "gatepass.notifications.gatepass-request-submitted-success-description-fott"
          : "gatepass.notifications.gatepass-request-submitted-success-description";
      notify({
        title: t("gatepass.notifications.gatepass-request-submitted-success-title"),
        description: t(successDescriptionKey),
        type: "success",
      });
    },
    onError: (error) => {
      if (shouldResetUploadedDocuments(error)) {
        useUploadStore.getState().clearFilesForKey(DELIVERY_NOTES_FIELD_KEY);
      }

      notify({
        title: t("gatepass.notifications.gatepass-request-submitted-error-title"),
        description:
          getSubmitErrorMessage(error) ||
          t("gatepass.notifications.gatepass-request-submitted-error-description"),
        type: "error",
      });
      onSubmitError?.(error);
    },
  });

  const onSubmit = async (): Promise<void> => {
    if (useUploadStore.getState().hasAnyUploadInFlight()) {
      onSubmitError?.(new Error("upload in flight"));
      return;
    }

    const formValues = getFormValues();
    if (hasMissingDeliveryNoteUrls(formValues)) {
      notify({
        title: t("gatepass.notifications.gatepass-request-upload-error-title"),
        description: t("gatepass.notifications.gatepass-request-upload-error-description"),
        type: "error",
      });
      onSubmitError?.(new Error("delivery notes upload incomplete"));
      return;
    }

    const resolvedSecurityClearanceId =
      securityClearanceId ||
      String((formValues as { securityClearanceId?: string }).securityClearanceId ?? "");
    const payload: NormalGatepassRequestPayload = mapGatepassFormToPayload(
      formValues,
      resolvedSecurityClearanceId,
      { isIndividualUser },
    );
    gatepassRequest.mutate(payload);
  };
  return { onSubmit };
};
